import { useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { requireClient, getClient } from '@/lib/matrix/client'
import { Avatar } from '@/components/ui/Avatar'
import { BridgeBadge } from '@/components/ui/BridgeBadge'
import { formatTime, formatDate } from '@/lib/utils/time'
import { useRoomStore, type RoomSummary } from '@/lib/store/room-store'
import { getSpaces } from '@/lib/matrix/spaces'
import { useNavigate } from 'react-router'

interface SearchResult {
  eventId: string
  roomId: string
  sender: string
  body: string
  timestamp: number
  roomName: string
}

interface SearchPanelProps {
  onClose: () => void
}

function getSenderDisplay(sender: string): string {
  const match = sender.match(/^@(.+?):/)
  return match?.[1] ?? sender
}

export function SearchPanel({ onClose }: SearchPanelProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)
  const [selected, setSelected] = useState(0)
  const { rooms, setActiveRoom } = useRoomStore()
  const navigate = useNavigate()

  // Live room/DM jump list — client-side, so it covers every joined room
  // (including encrypted ones) instantly.
  const roomMatches = useMemo(() => {
    const client = getClient()
    const spaceIds = client ? getSpaces(client).spaceIds : new Set<string>()
    const q = query.trim().toLowerCase()
    let list = Array.from(rooms.values()).filter((r) => r.membership === 'join' && !spaceIds.has(r.roomId))
    if (q) list = list.filter((r) => r.name.toLowerCase().includes(q))
    list.sort((a, b) => (b.lastEvent?.timestamp ?? 0) - (a.lastEvent?.timestamp ?? 0))
    return list.slice(0, 8)
  }, [rooms, query])

  useEffect(() => setSelected(0), [query])

  const openRoom = (roomId: string) => {
    setActiveRoom(roomId)
    navigate(`/room/${encodeURIComponent(roomId)}`)
    onClose()
  }

  const searchMessages = async () => {
    if (!query.trim()) return
    setSearching(true)
    setSearched(true)
    try {
      const client = requireClient()
      const response = await client.searchRoomEvents({ term: query.trim() })
      const mapped: SearchResult[] = (response.results ?? []).map((r) => {
        const evt = r.context.ourEvent
        const roomId = evt.getRoomId() ?? ''
        return {
          eventId: evt.getId() ?? '',
          roomId,
          sender: evt.getSender() ?? '',
          body: evt.getContent()?.body ?? '',
          timestamp: evt.getTs(),
          roomName: rooms.get(roomId)?.name ?? roomId,
        }
      })
      setResults(mapped)
    } catch (err) {
      console.error('Search failed:', err)
      setResults([])
    } finally {
      setSearching(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { onClose(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((i) => Math.min(i + 1, roomMatches.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelected((i) => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') {
      e.preventDefault()
      if (roomMatches[selected]) openRoom(roomMatches[selected].roomId)
      else searchMessages()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg shadow-lg animate-slide-up max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[rgb(var(--np-border))]">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--np-tx-muted))]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Jump to a chat, or search messages…"
              autoFocus
              className="w-full bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] rounded-md pl-10 pr-3 py-2.5 text-sm text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)] transition-all"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll">
          {/* Rooms / quick switcher */}
          {roomMatches.length > 0 && (
            <div className="py-1">
              <p className="label-section px-4 pt-2 pb-1">Chats</p>
              {roomMatches.map((room, i) => (
                <RoomRow key={room.roomId} room={room} active={i === selected} onClick={() => openRoom(room.roomId)} onHover={() => setSelected(i)} />
              ))}
            </div>
          )}

          {/* Message search trigger */}
          {query.trim() && (
            <button
              onClick={searchMessages}
              className="w-full text-left px-4 py-2.5 border-t border-[rgb(var(--np-border))] hover:bg-[rgb(var(--np-raised))] transition-colors flex items-center gap-2 text-sm text-[rgb(var(--np-tx-secondary))]"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              Search messages for “<span className="font-medium text-[rgb(var(--np-tx-primary))]">{query.trim()}</span>”
            </button>
          )}

          {/* Message results */}
          {searching && <div className="p-6 text-center text-sm text-[rgb(var(--np-tx-muted))]">Searching messages…</div>}
          {!searching && searched && (
            <div>
              <p className="label-section px-4 pt-3 pb-1">Messages</p>
              {results.length === 0 ? (
                <p className="px-4 pb-3 text-xs text-[rgb(var(--np-tx-muted))]">No message results.</p>
              ) : (
                results.map((result) => (
                  <button
                    key={result.eventId}
                    onClick={() => openRoom(result.roomId)}
                    className="w-full text-left px-4 py-3 hover:bg-[rgb(var(--np-raised))] border-t border-[rgb(var(--np-border))] transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Avatar name={getSenderDisplay(result.sender)} userId={result.sender} size={20} />
                      <span className="text-xs font-semibold text-[rgb(var(--np-tx-primary))]">{getSenderDisplay(result.sender)}</span>
                      <span className="text-[10px] text-[rgb(var(--np-tx-muted))]">in {result.roomName}</span>
                      <span className="text-[10px] font-data text-[rgb(var(--np-tx-muted))] ml-auto">{formatDate(result.timestamp)} {formatTime(result.timestamp)}</span>
                    </div>
                    <p className="text-sm text-[rgb(var(--np-tx-secondary))] truncate">{result.body}</p>
                  </button>
                ))
              )}
              <p className="px-4 py-2 text-[10px] text-[rgb(var(--np-tx-muted)/0.8)] border-t border-[rgb(var(--np-border))]">
                Message search covers unencrypted rooms only — encrypted chats (WhatsApp, Signal) aren’t indexed by the server.
              </p>
            </div>
          )}

          {!query.trim() && roomMatches.length === 0 && (
            <p className="p-6 text-center text-sm text-[rgb(var(--np-tx-muted))]">Type to jump to a chat or search messages.</p>
          )}
        </div>
      </div>
    </div>
  )
}

function RoomRow({ room, active, onClick, onHover }: { room: RoomSummary; active: boolean; onClick: () => void; onHover: () => void }) {
  return (
    <button
      onClick={onClick}
      onMouseMove={onHover}
      className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-colors ${active ? 'bg-[rgb(var(--np-raised))]' : 'hover:bg-[rgb(var(--np-raised)/0.5)]'}`}
    >
      {room.isDirect
        ? <Avatar mxc={room.avatarUrl} name={room.name} userId={room.roomId} size={24} square />
        : <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-md bg-[rgb(var(--np-raised))] text-[rgb(var(--np-tx-muted))]"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></svg></span>}
      <span className="flex-1 min-w-0 text-sm text-[rgb(var(--np-tx-primary))] truncate">{room.name}</span>
      {room.bridgeType && <BridgeBadge type={room.bridgeType} size={12} />}
      {room.unreadCount > 0 && <span className="unread-pill font-data">{room.unreadCount > 99 ? '99+' : room.unreadCount}</span>}
    </button>
  )
}
